import { apiClient } from '@/api/client'
import type {
  ApiKeyConfig,
  ApiKeyRecord,
  CompanyDetail,
  CreateApiKeyResult,
  RotateApiKeyResult,
  SiteAccessMode,
  SiteInventoryCompany,
  SubscriptionTier,
} from './companyDetailTypes'

export async function fetchCompanyDetail(clientId: string): Promise<CompanyDetail> {
  const { data } = await apiClient.get<CompanyDetail>(`/portal/clients/api/v2/${clientId}`)
  return data
}

/** API-key routes for one company's keys, or (clientId null) for global
 * keys, which have no owning company and their own route prefix. */
function keysBase(clientId: string | null): string {
  return clientId === null ? '/portal/clients/api/v2/global-api-keys' : `/portal/clients/api/v2/${clientId}/api-keys`
}

export async function fetchApiKeys(clientId: string | null): Promise<ApiKeyRecord[]> {
  const { data } = await apiClient.get<ApiKeyRecord[]>(keysBase(clientId))
  return data
}

export async function createApiKey(
  clientId: string | null,
  params: {
    name: string
    subscription_tier: SubscriptionTier
    site_access_mode: SiteAccessMode
    controller_ids: string[]
    daily_access: boolean
    granular_access: boolean
  },
): Promise<CreateApiKeyResult> {
  const { data } = await apiClient.post<CreateApiKeyResult>(keysBase(clientId), params)
  return data
}

export async function revokeApiKey(clientId: string | null, keyId: string): Promise<void> {
  await apiClient.post(`${keysBase(clientId)}/${keyId}/revoke`)
}

export async function rotateApiKey(
  clientId: string | null,
  keyId: string,
  gracePeriodDays: number,
): Promise<RotateApiKeyResult> {
  const { data } = await apiClient.post<RotateApiKeyResult>(`${keysBase(clientId)}/${keyId}/rotate`, {
    grace_period_days: gracePeriodDays,
  })
  return data
}

/** PATCH .../<key_id>/config - plan, endpoint permissions and sites in one
 * atomic save. Rejected with 409 if someone else saved since
 * `configVersion` was loaded. The secret is unchanged. */
export async function updateApiKeyConfig(
  clientId: string | null,
  keyId: string,
  configVersion: number,
  config: ApiKeyConfig,
): Promise<ApiKeyRecord> {
  const { data } = await apiClient.patch<{ record: ApiKeyRecord }>(`${keysBase(clientId)}/${keyId}/config`, {
    config_version: configVersion,
    ...config,
    controller_ids: config.site_access_mode === 'selected' ? config.controller_ids : [],
  })
  return data.record
}

export async function deleteApiKey(clientId: string | null, keyId: string): Promise<void> {
  await apiClient.delete(`${keysBase(clientId)}/${keyId}`)
}

export async function fetchSiteInventory(): Promise<SiteInventoryCompany[]> {
  const { data } = await apiClient.get<SiteInventoryCompany[]>('/portal/clients/api/v2/api-key-site-inventory')
  return data
}

export async function addUserToCompany(clientId: string, userId: string): Promise<void> {
  await apiClient.post(`/portal/clients/add-user/${clientId}/${userId}`)
}

export async function removeUserFromCompany(clientId: string, userId: string): Promise<void> {
  await apiClient.post(`/portal/clients/remove-user/${clientId}/${userId}`)
}

export async function addControllerToCompany(clientId: string, controllerId: string): Promise<void> {
  await apiClient.get(`/portal/clients/add-controller-to-company/${clientId}/${controllerId}`)
}

export async function removeControllerFromCompany(clientId: string, controllerId: string): Promise<void> {
  await apiClient.post(`/portal/clients/remove-controller-from-company/${clientId}/${controllerId}`)
}

export async function deleteCompany(clientId: string): Promise<void> {
  await apiClient.get(`/portal/clients/delete/${clientId}`)
}

/** New capability, not a legacy restoration - legacy's company_details.html
 * never had a way to rename a company or change its capacity after
 * creation, only create/delete. */
export async function updateCompany(
  clientId: string,
  params: { company_name?: string; capacity?: string },
): Promise<{ company_id: string; company_name: string; capacity: string | null }> {
  const { data } = await apiClient.post(`/portal/clients/update/${clientId}`, params)
  return data
}

/** Scopes a specific user to one of their company's controllers (a
 * user_controllers row) - separate from company-level attachment. `controllerId`
 * must already belong to the user's company; the backend doesn't itself
 * enforce that (neither did the legacy route this ports), so the caller's
 * "add" dropdown is what keeps the choice scoped, matching legacy's own
 * company.controllers-only picker. */
export async function addControllerToUser(userId: string, controllerId: string): Promise<void> {
  await apiClient.post(`/portal/clients/add-controller-user/${userId}/${controllerId}`)
}

export async function removeControllerFromUser(userId: string, controllerId: string): Promise<void> {
  await apiClient.get(`/portal/clients/delete-controller-user/${userId}/${controllerId}`)
}
